<template>
  <div class="program-tab">
    <!-- Loading overlay -->
    <Loading
      v-if="isLoading"
      :loading="isLoading"
      loadingText="Loading programs..."
    />

    <el-tabs v-else v-model="activeSubTab" class="program-sub-tabs">
      <el-tab-pane label="All Programs" name="all">
        <div class="program-scroll-area">
          <!-- Non-enrolled programs -->
          <div class="program-grid enrolled-grid" :class="{ 'loading': isLoading }">
            <div
              v-for="program in nonEnrolledActivePrograms"
              :key="program.id"
              :class="[
                'program-card',
                { 'is-selected': isProgramSelected(program.id) },
              ]"
              @click="handleCardClick(program)"
            >
              <div class="card-top-row">
                <img
                  :src="getCheckboxImage(isProgramSelected(program.id))"
                  alt="checkbox"
                  class="checkbox-icon"
                >
                <p class="card-name">{{ program.name }}</p>
              </div>
              <p v-if="program.description" class="card-desc">{{ program.description }}</p>
              <p class="card-meta">
                <span v-if="program.price !== undefined && program.price !== null">Price: {{ formatPrice(program.price) }}</span>
                <span v-if="program.price != null && program.duration" class="meta-dot">&bull;</span>
                <span v-if="program.duration">Duration: {{ formatDuration(program.duration) }}</span>
              </p>
            </div>
          </div>

          <!-- Enrolled programs: single-column grid -->
          <div v-if="enrolledActivePrograms.length > 0" class="program-grid enrolled-grid" :class="{ 'loading': isLoading }">
            <div
              v-for="program in enrolledActivePrograms"
              :key="program.id"
              class="program-card is-enrolled"
            >
              <div class="card-top-row">
                <img
                  :src="prefix.allCheck"
                  alt="checkbox"
                  class="checkbox-icon checkbox-enrolled"
                >
                <p class="card-name">{{ program.name }}</p>
                <span class="enrolled-tag">Enrolled</span>
              </div>
              <p v-if="program.description" class="card-desc">{{ program.description }}</p>
              <p class="card-meta">
                <span v-if="program.price !== undefined && program.price !== null">Price: {{ formatPrice(program.price) }}</span>
                <span v-if="program.price != null && program.duration" class="meta-dot">&bull;</span>
                <span v-if="program.duration">Duration: {{ formatDuration(program.duration) }}</span>
              </p>
              <div v-if="getEnrollmentData(program.id)" class="progress-section">
                <div class="progress-item">
                  <p class="progress-label">{{ getStepsCompleted(program) }}/{{ getStepsTotal(program) }} steps completed ({{ getStepsPercentage(program) }}%)</p>
                  <div class="progress-bar-container">
                    <div class="progress-bar" :style="{ width: getStepsPercentage(program) + '%' }"></div>
                  </div>
                </div>
                <div class="progress-item">
                  <p class="progress-label">{{ getDaysRemaining(program) }}/{{ getDaysTotal(program) }} days remaining ({{ getDaysPercentage(program) }}%)</p>
                  <div class="progress-bar-container">
                    <div class="progress-bar" :style="{ width: getDaysPercentage(program) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!isLoading && allActivePrograms.length === 0" class="empty-state">
            <p class="empty-text">No programs available.</p>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane label="Enrolled Programs" name="enrolled">
        <div class="program-scroll-area">
          <div v-if="enrolledPrograms.length > 0" class="program-grid enrolled-grid" :class="{ 'loading': isLoading }">
            <div
              v-for="program in enrolledPrograms"
              :key="program.id"
              class="program-card is-enrolled"
            >
              <!-- Row 1: checkbox + name + enrolled tag -->
              <div class="card-top-row">
                <img
                  :src="prefix.allCheck"
                  alt="checkbox"
                  class="checkbox-icon checkbox-enrolled"
                >
                <p class="card-name">{{ program.name }}</p>
                <span class="enrolled-tag">Enrolled</span>
              </div>

              <!-- Description -->
              <p v-if="program.description" class="card-desc">{{ program.description }}</p>

              <!-- Price + Duration -->
              <p class="card-meta">
                <span v-if="program.price !== undefined && program.price !== null">Price: {{ formatPrice(program.price) }}</span>
                <span v-if="program.price != null && program.duration" class="meta-dot">&bull;</span>
                <span v-if="program.duration">Duration: {{ formatDuration(program.duration) }}</span>
              </p>

              <!-- Progress Bars for enrolled programs -->
              <div v-if="getEnrollmentData(program.id)" class="progress-section">
                <div class="progress-item">
                  <p class="progress-label">{{ getStepsCompleted(program) }}/{{ getStepsTotal(program) }} steps completed ({{ getStepsPercentage(program) }}%)</p>
                  <div class="progress-bar-container">
                    <div class="progress-bar" :style="{ width: getStepsPercentage(program) + '%' }"></div>
                  </div>
                </div>
                <div class="progress-item">
                  <p class="progress-label">{{ getDaysRemaining(program) }}/{{ getDaysTotal(program) }} days remaining ({{ getDaysPercentage(program) }}%)</p>
                  <div class="progress-bar-container">
                    <div class="progress-bar" :style="{ width: getDaysPercentage(program) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!isLoading && enrolledPrograms.length === 0" class="empty-state">
            <p class="empty-text">No enrolled programs.</p>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { getWorkflowsApi } from '@/api/workflowManage';
import { mapState, mapGetters } from 'vuex';
import Loading from '@/NewLibrary/Loading/Loading.vue';

export default {
  name: "ProgramTab",
  components: { Loading },
  emits: ['selection-change'],
  props: {
    patientId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      isLoading: false,
      activeSubTab: 'all',
      prefix: {},
    };
  },
  computed: {
    ...mapState('orderTest/program', ['selectedProgramIdList', 'enrolledProgramIds', 'enrollmentDataMap']),
    ...mapGetters('orderTest/program', ['activeProgramList', 'enrolledProgramList']),
    allActivePrograms() {
      return this.activeProgramList;
    },
    nonEnrolledActivePrograms() {
      return this.activeProgramList.filter(p => !this.enrolledProgramIds.includes(p.id));
    },
    enrolledActivePrograms() {
      return this.activeProgramList.filter(p => this.enrolledProgramIds.includes(p.id));
    },
    enrolledPrograms() {
      return this.enrolledProgramList;
    },
  },
  watch: {
    patientId(newVal) {
      if (newVal) {
        this.$store.dispatch('orderTest/program/fetchEnrollmentData', newVal);
      }
    },
  },
  mounted() {
    this.prefix = this.getPrefixPicture();
    this.fetchPrograms();
  },
  methods: {
    async fetchPrograms() {
      this.isLoading = true;
      try {
        const response = await getWorkflowsApi({ is_program: true });
        const programList = Array.isArray(response.data) ? response.data : [];

        this.$store.dispatch('orderTest/program/setProgramList', programList);

        if (this.patientId) {
          await this.$store.dispatch('orderTest/program/fetchEnrollmentData', this.patientId);
        }
      } catch (error) {
        console.error('Failed to fetch programs:', error);
        this.$store.dispatch('orderTest/program/setProgramList', []);
      } finally {
        this.isLoading = false;
      }
    },

    isEnrolled(programId) {
      return this.enrolledProgramIds.includes(programId);
    },

    isProgramSelected(programId) {
      return this.selectedProgramIdList.includes(programId);
    },

    handleCardClick(program) {
      if (this.isEnrolled(program.id)) {
        return;
      }

      if (this.isProgramSelected(program.id)) {
        this.$store.dispatch('orderTest/program/removeSelectedProgram', program.id);
      } else {
        this.$store.dispatch('orderTest/program/addSelectedProgram', program.id);
      }

      const selectedPrograms = this.activeProgramList.filter(p => this.selectedProgramIdList.includes(p.id));
      const totalPrice = selectedPrograms.reduce((sum, p) => sum + (p.price || 0), 0);

      const cartItems = selectedPrograms.map(programItem => ({
        id: programItem.id,
        bundleId: programItem.id,
        tab: 'program',
        name: programItem.name,
        displayName: programItem.name,
        description: programItem.description || '',
        price: programItem.price || 0,
        category: 'program',
        isHovered: false,
        originalItem: programItem,
      }));

      this.$emit('selection-change', cartItems, totalPrice);
    },

    getCheckboxImage(isChecked) {
      return isChecked ? this.prefix.allCheck : this.prefix.unCheck;
    },

    getPrefixPicture() {
      return {
        unCheck: require('@/assets/orderTest/check_box_outline_blank_24px.svg'),
        allCheck: require('@/assets/orderTest/Checkbox(all).svg'),
      };
    },

    formatPrice(price) {
      if (price === undefined || price === null) return '';
      const symbol = this.$store.state.currency?.symbol || '$';
      return `${symbol}${Number(price).toFixed(2)}`;
    },

    getEnrollmentData(programId) {
      return this.enrollmentDataMap[programId] || null;
    },

    getStepsCompleted(program) {
      const enrollment = this.getEnrollmentData(program.id);
      if (!enrollment) return 0;
      if (enrollment.status === 'completed') return this.getStepsTotal(program);
      return enrollment.program_steps_executed || 0;
    },

    getStepsTotal(program) {
      const enrollment = this.getEnrollmentData(program.id);
      return enrollment?.program_steps_total || program.steps_count || 0;
    },

    getStepsPercentage(program) {
      const total = this.getStepsTotal(program);
      if (!total) return 0;
      return Math.round((this.getStepsCompleted(program) / total) * 100);
    },

    getDateProgress(program) {
      const enrollment = this.getEnrollmentData(program.id);
      if (!enrollment) return { totalDays: 0, remainingDays: 0, elapsedDays: 0 };

      const startDate = enrollment.program_start_date;
      const endDate = enrollment.program_end_date;

      if (!startDate || !endDate) return { totalDays: 0, remainingDays: 0, elapsedDays: 0 };

      const start = new Date(startDate);
      const end = new Date(endDate);
      const now = new Date();

      const totalMs = end.getTime() - start.getTime();
      const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));

      if (now < start) return { totalDays, remainingDays: totalDays, elapsedDays: 0 };
      if (now > end) return { totalDays, remainingDays: 0, elapsedDays: totalDays };

      const elapsedDays = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      const remainingDays = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      return { totalDays, remainingDays, elapsedDays };
    },

    getDaysTotal(program) {
      return this.getDateProgress(program).totalDays;
    },

    getDaysRemaining(program) {
      return this.getDateProgress(program).remainingDays;
    },

    getDaysPercentage(program) {
      const { totalDays, elapsedDays } = this.getDateProgress(program);
      if (!totalDays) return 0;
      return Math.round((elapsedDays / totalDays) * 100);
    },

    formatDuration(duration) {
      if (!duration) return '';
      if (duration.type === 'rolling') {
        const { value, unit } = duration;
        if (!value || !unit) return '';
        return `${value} ${unit}`;
      }
      if (duration.type === 'fixed') {
        const start = duration.start_date ? new Date(duration.start_date).toLocaleDateString() : '';
        const end = duration.end_date ? new Date(duration.end_date).toLocaleDateString() : '';
        if (start && end) return `${start} - ${end}`;
        return '';
      }
      // Fallback for simple {value, unit}
      const { value, unit } = duration;
      if (!value || !unit) return '';
      return `${value} ${unit}`;
    },
  },
};
</script>

<style scoped>
.program-tab {
  width: 100%;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
}

/* Pill-style sub-tabs (matches order-test-tabs pattern) */
.program-sub-tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.program-sub-tabs :deep(.el-tabs__header) {
  margin-bottom: 12px;
  background: none;
  border-bottom: none;
}

.program-sub-tabs :deep(.el-tabs__nav-wrap::after) {
  display: none;
}

.program-sub-tabs :deep(.el-tabs__nav) {
  display: flex;
  align-items: center;
  gap: 10px;
}

.program-sub-tabs :deep(.el-tabs__item) {
  display: flex;
  padding: 4px 16px !important;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  background: rgba(211, 241, 243, 0.35);
  color: var(--SecondaryBlack, #666);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  border: none;
  margin: 0;
  transition: all 0.2s ease;
  height: auto;
}

.program-sub-tabs :deep(.el-tabs__item:hover) {
  color: var(--TrueBlack, #333);
  background: rgba(211, 241, 243, 0.55);
}

.program-sub-tabs :deep(.el-tabs__item.is-active) {
  background: #D3F1F3;
  color: var(--TrueBlack, #333);
}

.program-sub-tabs :deep(.el-tabs__active-bar) {
  display: none;
}

.program-sub-tabs :deep(.el-tabs__content) {
  flex: 1;
  overflow: hidden;
}

.program-sub-tabs :deep(.el-tab-pane) {
  height: 100%;
}

.program-scroll-area {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
  scrollbar-color: #c8d2de transparent;
  border: 1px dashed #C4C4C4;
  border-radius: 8px;
  padding: 12px;
}

.program-scroll-area::-webkit-scrollbar {
  width: 6px;
  height: 6px;
  background: transparent;
}

.program-scroll-area::-webkit-scrollbar-track {
  background: transparent;
}

.program-scroll-area::-webkit-scrollbar-thumb {
  background: #c8d2de;
  border-radius: 999px;
}

.program-scroll-area::-webkit-scrollbar-thumb:hover {
  background: #aebcca;
}

.program-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 0;
}

.program-card {
  background: transparent;
  border: 1px solid #E0E0E0;
  border-radius: 10px;
  padding: 24px;
  min-height: 132px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  overflow: hidden;
}

.program-card:hover {
  border-color: #bbb;
}

.is-selected {
  border-color: #004879;
  background: #EDF4FA;
}

.is-enrolled {
  opacity: 0.55;
  cursor: not-allowed;
}

.is-enrolled:hover {
  border-color: #E0E0E0;
}

/* Row 1: checkbox + name + tag */
.card-top-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.checkbox-icon {
  height: 20px;
  width: 20px;
  min-width: 20px;
  flex-shrink: 0;
  filter: brightness(0) saturate(100%) invert(8%) sepia(100%) saturate(3500%) hue-rotate(190deg);
}

.checkbox-enrolled {
  filter: brightness(0) saturate(100%) invert(50%) sepia(0%) saturate(0%) hue-rotate(0deg);
}

.card-name {
  flex: 1;
  min-width: 0;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.enrolled-tag {
  flex-shrink: 0;
  background: #E8F5E9;
  color: #2E7D32;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;
}

.card-desc {
  width: 100%;
  color: #666;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.card-meta {
  width: 100%;
  color: #999;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-dot {
  color: #CCC;
}


.enrolled-grid {
  grid-template-columns: 1fr;
}

.program-grid.loading {
  opacity: 0.3;
  pointer-events: none;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 24px 20px;
  text-align: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-text {
  font-size: 14px;
  color: #C4C4C4;
  margin: 0;
}

/* Progress Bars */
.progress-section {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
}

.progress-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.progress-label {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #333333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
}

.progress-bar-container {
  width: 100%;
  height: 4px;
  background: #E0E0E0;
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #004879;
  border-radius: 2px;
  transition: width 0.3s ease;
}
</style>
